/**
 * 内容加载失败时的全站提示条。
 *
 * 为什么做成全局拦截器而不是逐页加错误态：
 *   七个页面各自 `catch (error) { console.error(...) }` 之后就什么也不做，
 *   于是 API 全挂时用户看到的是**一个结构完整、但一条内容都没有的页面** ——
 *   不报错、不提示，从外面完全看不出站点坏了。
 *   2026-09-12 全部 /api/* 返回 502，服务、职位、团队、案例、新闻五个页面
 *   全空，就是这样过了很久没被发现。
 *
 *   逐页补错误态要动七处布局各异的 JSX；一个 axios 响应拦截器只需一处，
 *   且以后新增的页面自动覆盖。
 *
 * 只对 /api 的请求、且只对 5xx 与网络错误提示。4xx 是业务预期内的结果
 * （比如找不到某篇文章），不该弹全站横幅。
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './ApiStatusBanner.css';

/**
 * 一次失败要不要弹横幅。
 *
 * 导出是为了可被直接测试 —— 把判定内联在拦截器里的话，测试只能重写一遍
 * 同样的条件，那验证的是副本而不是真正跑的代码。
 */
export function shouldWarn(url, response) {
  // 无 response = 网络层失败；5xx = 服务端故障。4xx 是业务预期内的结果
  // （比如找不到某篇文章），不该弹全站横幅。
  return String(url || '').includes('/api') && (!response || response.status >= 500);
}

export default function ApiStatusBanner() {
  const { t } = useTranslation('common');
  const [down, setDown] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => {
        // 任何一次 /api 成功都说明后端回来了，收起横幅
        if (String(res.config?.url || '').includes('/api')) setDown(false);
        return res;
      },
      (err) => {
        if (shouldWarn(err.config?.url, err.response)) {
          setDown(true);
          setDismissed(false);
        }
        return Promise.reject(err);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  if (!down || dismissed) return null;

  return (
    <div className="api-status-banner" role="status" aria-live="polite">
      <span>{t('apiStatus.message')}</span>
      <button type="button" onClick={() => setDismissed(true)} aria-label={t('apiStatus.dismiss')}>
        ×
      </button>
    </div>
  );
}
